// ============================================================================
// OCPI-Admin API Types (New Unified API)
// ============================================================================

/**
 * Connection type enum for OCPI connections
 */
export type ConnectionType = 'OCPI' | 'Hubject' | 'Gireve' | 'Kiosk' | 'OcpiToVpost';

/**
 * CPO Type - Internal (Nayax manages infrastructure) or External (operator has own OCPI system)
 * This is a one-time choice during onboarding and cannot be changed later.
 */
export type CpoType = 'internal' | 'external';

/**
 * CSMS Type - Charging Station Management System type from backend
 * Maps to CpoType for UI display:
 * - NAYAX_ENERGY → internal (Nayax manages infrastructure, can publish locations/tariffs)
 * - EXTERNAL → external (operator has own OCPI system, limited functionality)
 */
export type Csms = 'NAYAX_ENERGY' | 'EXTERNAL';

/**
 * Response from GET /ocpi-admin/ocpi-cpo/{nayaxOperatorId}/ocpi-overview
 * Single endpoint that provides operator's OCPI configuration and connections
 */
export interface CpoOcpiOverview {
  /** OCPI party identification — present for Internal CPO, absent for External CPO */
  ocpi_id?: OcpiPartyIdentification;
  /** Charging Station Management System type */
  csms: Csms;
  /** Connections for Internal CPO operators */
  connections?: ProtocolConnection[];
  /** PTP connections for External CPO operators (Kiosk, OcpiToVpost) */
  ptp_connections?: ProtocolConnection[];
}

/**
 * Helper to map CSMS to CpoType for UI compatibility
 */
export const csmsToCpoType = (csms: Csms): CpoType => {
  return csms === 'NAYAX_ENERGY' ? 'internal' : 'external';
};

/**
 * OCPI Protocol Version enum
 */
export type OcpiVersion = '2.1.1' | '2.2' | '2.2.1';

/**
 * Request body for POST /external-ocpi-cpo/{operatorId}/connections/nayax-kiosk/full-setup
 * Used to configure external CPO connection
 */
export interface ExternalCpoConnectionRequestDto {
  connection_version: OcpiVersion;
  versions_url?: string;
  token_a?: string;
  csms_name: string;
}

/**
 * Request body for PUT /external-ocpi-cpo/{operatorId}/connections/ocpi-to-vpost
 * Same fields as kiosk connection plus csms_name (required)
 */
export interface VpostConnectionRequestDto {
  connection_version: OcpiVersion;
  versions_url?: string;
  token_a?: string;
  csms_name: string;
}

/**
 * OCPI Party Identification
 */
export interface OcpiPartyIdentification {
  partyId: string;
  countryCode: string | null;
}

/**
 * OCPI Party Info containing identification and name
 */
export interface OcpiPartyInfo {
  id: OcpiPartyIdentification;
  name: string;
}

/**
 * Protocol Connection response from GET /connections endpoint
 * Base type with discriminator for OCPI vs OICP connections
 */
export interface ProtocolConnection {
  id: number;
  partner?: OcpiPartyInfo;
  registered: boolean;
  type: ConnectionType;
  $type?: 'OCPIConnection' | 'OICPConnection'; // JSON discriminator

  // OCPI Connection specific fields
  protocol_version?: string;              // e.g., "OCPI-2.2.1"
  local_authorization_token?: string;     // Local auth token (UUID)
  remote_authorization_token?: string;    // Remote auth token (UUID)
}

/**
 * Request body for PUT /ocpi-cpo/{operatorId} - Update CPO Party
 */
export interface OcpiPartyDto {
  ocpi_id: OcpiPartyIdentification;
  name: string;
}

// ============================================================================
// Publish Response Types
// ============================================================================

/**
 * Published tariff information with actor context
 */
export interface PublishedTariffInfo {
  relationId: number;
  tariffType: string; // e.g., "Energy", "Time", "Flat"
  actorId: number | null;
  actorName: string | null;
  messageId: string;
}

/**
 * Published location information with actor context
 */
export interface PublishedLocationInfo {
  actorId: number;
  actorName: string | null;
  machineCount: number;
  messageId: string;
}

/**
 * CPO sync response from the backend (incremental with retries, then full-sync fallback)
 */
export interface CpoSyncOutcomeResponse {
  incremental_attempts: number;
  fell_back: boolean;
}

export interface CpoSyncOutcome {
  /** Number of incremental attempts the backend made */
  incrementalAttempts: number;
  /** True when the backend ran a full sync because every incremental attempt failed */
  fellBack: boolean;
}

/**
 * Base publish response for tariffs
 */
export interface PublishTariffResponse {
  requestId: string;
  isSuccess: boolean;
  errorMessage: string;
  errorCode: string;
  exception: string;
  messageIds: string[];
  publishedTariffs: PublishedTariffInfo[]; // NEW: detailed tariff info
  errors: string[];
}

/**
 * Machine-level error details
 */
export interface MachineError {
  machineId: number;
  machineName: string | null; // NEW: machine name for display
  errors: string[];
}

/**
 * Machine-level warning details
 */
export interface MachineWarning {
  machineId: number;
  machineName: string | null; // NEW: machine name for display
  warnings: string[];
}

/**
 * Actor-level error with nested machine errors
 */
export interface ActorError {
  actorId: number;
  actorName: string | null; // NEW: actor name for display
  machineErrors: MachineError[];
  errors: string[];
}

/**
 * Actor-level warning with nested machine warnings
 */
export interface ActorWarning {
  actorId: number;
  actorName: string | null; // NEW: actor name for display
  machineWarnings: MachineWarning[];
  warnings: string[];
}

/**
 * OCPI Summary errors containing actor and machine level details
 */
export interface OcpiSummaryErrors {
  actorErrors: ActorError[];
  warnings: ActorWarning[];
}

/**
 * Extended publish response for locations with detailed error info
 */
export interface PublishLocationsResponse extends PublishTariffResponse {
  publishedLocations: PublishedLocationInfo[]; // NEW: detailed location info
  ocpiSummaryErrors: OcpiSummaryErrors;
  successMachineIds: number[];
  failedMachineIds: number[];
}

/**
 * Single location publish response (used by publishlocation endpoint)
 */
export interface PublishSingleLocationResponse {
  requestId: string;
  isSuccess: boolean;
  errorMessage: string;
  errorCode: string;
  exception: string;
  messageId: string;
  actorId: number; // NEW: actor ID
  actorName: string | null; // NEW: actor name for display
  machineCount: number; // NEW: number of machines published
  errors: OcpiSummaryErrors | null;
  successMachineIds: number[];
  failedMachineIds: number[];
}

// ============================================================================
// Legacy Types (kept for backward compatibility during migration)
// ============================================================================

/**
 * @deprecated Use ProtocolConnection instead
 */
export interface OcpiCpoInfoResponse {
  ocpiPartyIdentification: {
    partyId: string;
    countryCode: string;
  };
  subscribedOcpiModules: string[];
}

/**
 * @deprecated Legacy connection type
 */
export interface OcpiConnection {
  connectionId: string;
  role: 'CPO' | 'EMSP';
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING';
  versionUrl: string;
  authToken: string;
  lastSync: string;
}

/**
 * @deprecated Use OCPI modules from ProtocolConnection
 */
export interface OcpiModules {
  locations: boolean;
  tariffs: boolean;
  sessions: boolean;
  cdrs: boolean;
  commands: boolean;
  tokens: boolean;
}

/**
 * @deprecated No longer used with new API
 */
export interface RefreshOcpiInfoResponse {
  success: boolean;
  message: string;
  lastRefresh: string;
}

/**
 * @deprecated No longer used with new API
 */
export interface OperatorMappingResponse {
  success: boolean;
  dcsOperatorId: number;
  infuseOperatorId: number;
}

/**
 * @deprecated Use OcpiPartyDto instead
 */
export interface OcpiCpoUpdateRequest {
  ocpi_id: {
    partyId: string;
    countryCode: string;
  };
  name: string;
}

/**
 * @deprecated No longer used with new API
 */
export interface OcpiCpoUpdateResponse {
  success: boolean;
  operatorId: number;
  ocpiId: {
    partyId: string;
    countryCode: string;
  };
}

// Type aliases for backward compatibility
export type UpdateOcpiCpoRequest = OcpiCpoUpdateRequest;
export type UpdateOcpiCpoResponse = OcpiCpoUpdateResponse;

/**
 * @deprecated Use ProtocolConnection instead
 */
export interface KioskOperatorResponse {
  id: number;
  countryCode: string | null;
  partyId: string | null;
  settings: string | null;
}

/**
 * @deprecated No longer needed with new API
 */
export interface KioskConfiguration {
  autoStart: boolean;
  sessionTimeout: number;
}

// ============================================================================
// Error Response Types
// ============================================================================

export interface ApiErrorResponse {
  error: string;
  operatorId?: number;
  details?: string;
  statusCode?: number;
}
